import React, { useState, useEffect } from "react";
import MeetingForm from "components/common/MeetingForm";
import { BiHomeAlt } from "react-icons/bi";
import { GoPlus } from "react-icons/go";
import { FiLoader } from "react-icons/fi";
import { motion } from "framer-motion";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import EventActionsDropdown from "components/dropdown/EventActionDropdown";
import "styles/admin.css";  
import EventViewModal from "components/modal/EventViewModal";

const API_BASE = "https://algoritm-api-edu.uz";

const api = axios.create({
  baseURL: API_BASE,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("accessToken");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const EventsControl = ({ goBack }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [viewOpen, setViewOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);

  const handleView = async (id) => {
    try {
      setViewLoading(true);
      setViewOpen(true);

      const res = await api.get(`/api/admin/Event/GetById/${id}`);
      const eventData = res.data?.data || res.data?.item || res.data;

      if (!eventData?.id) {
        toast.error("Tadbir topilmadi");
        setViewOpen(false);
        return;
      }

      setSelectedEvent({
        id: eventData.id,
        title: eventData.title || "",
        description: eventData.description || "",
        location: eventData.location || "",
        startTime: eventData.startTime,
        endTime: eventData.endTime,
        imageUrl: eventData.imageUrl
          ? eventData.imageUrl.startsWith("http")
            ? eventData.imageUrl
            : `${API_BASE}/${eventData.imageUrl}`
          : null,
        statusName: eventData.statusName || "Created",
      });
    } catch (err) {
      console.error("View error:", err);
      toast.error("Tadbir ma'lumotlarini yuklab bo‘lmadi");
      setViewOpen(false);
    } finally {
      setViewLoading(false);
    }
  };

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const res = await api.post("/api/admin/Event/GetAll", {
        page: 1,
        pageSize: 100,
      });
      const list = res.data?.rows || res.data || [];
      setEvents(list);
    } catch (err) {
      console.error(err);
      toast.error("Tadbirlar yuklanmadi");
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  const handleAdd = async (formData) => {
    const data = new FormData();

    data.append("Title", formData.title.trim());
    data.append("Location", formData.location.trim());
    data.append("Description", formData.description?.trim() || "");

    data.append("Time.StartTime", formData.startTime);
    if (formData.endTime) {
      data.append("Time.EndTime", formData.endTime);
    }

    if (!formData.image) {
      toast.error("Rasm yuklash majburiy!");
      return;
    }
    data.append("file", formData.image);

    console.log("YUBORILMOQDA:");
    for (let [k, v] of data.entries()) {
      console.log(k, v);
    }

    try {
      const response = await api.post("/api/admin/Event/Add", data);
      console.log("Javob:", response.data);

      toast.success("Tadbir muvaffaqiyatli qo‘shildi!");
      setShowForm(false);
      fetchEvents();
    } catch (err) {
      console.error("API xatosi:", err.response?.data);
      const errors = err.response?.data?.errors;
      const msg = errors
        ? Object.values(errors).flat().join(" • ")
        : err.response?.data?.message || "Xato yuz berdi";
      toast.error("Xato: " + msg);
    }
  };

  const handleEdit = async (id) => {
    try {
      const res = await api.get(`/api/admin/Event/GetById/${id}`);
      const event = res.data?.data || res.data?.item || res.data;

      if (!event?.id) {
        toast.error("Tadbir topilmadi");
        return;
      }

      setEditingEvent({
        id: event.id,
        title: event.title || "",
        startTime: event.startTime || "",
        endTime: event.endTime || "",
        location: event.location || "",
        description: event.description || "",
        active: event.active ?? true,
        imageUrl: event.imageUrl
          ? event.imageUrl.startsWith("http")
            ? event.imageUrl
            : `${API_BASE}/${event.imageUrl}`
          : null,
      });

      setShowForm(true);
    } catch (err) {
      toast.error("Tahrirlash uchun ma'lumot olinmadi");
    }
  };

  const handleUpdate = async (formData) => {
    if (!editingEvent?.id) return;

    const data = new FormData();

    data.append("Title", formData.title.trim());
    data.append("Location", formData.location.trim());
    data.append("Description", formData.description?.trim() || "");
    data.append("StartTime", formData.startTime);
    if (formData.endTime) data.append("EndTime", formData.endTime);

    if (formData.image instanceof File) {
      data.append("file", formData.image);
    }

    try {
      await api.put(`/api/admin/Event/Update/${editingEvent.id}`, data);
      toast.success("Tadbir yangilandi!");
      setShowForm(false);
      setEditingEvent(null);
      fetchEvents();
    } catch (err) {
      toast.error("Yangilashda xato");
    }
  };

  const handleSubmit = (formData) => {
    editingEvent ? handleUpdate(formData) : handleAdd(formData);
  };

  return (
    <>
      <ToastContainer position="top-right" autoClose={4000} theme="colored" />

      <div className="banner-page-wrapper">
        <motion.div
          className="banner-header"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <motion.div className="breadcrumb" whileHover={{ scale: 1.02 }}>
            <motion.div
              className="home-icon"
              whileHover={{ rotate: 360 }}
              onClick={goBack}
            >
              <BiHomeAlt />
            </motion.div>
            <span>/ Tadbirlar boshqaruvi</span>
          </motion.div>

          <motion.button
            className="add-btn"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => {
              setEditingEvent(null);
              setShowForm(true);
            }}
          >
            <GoPlus />
            Tadbir qo‘shish
          </motion.button>
        </motion.div>

        {showForm ? (
          <MeetingForm
            initialData={editingEvent}
            onSubmit={handleSubmit}
            onCancel={() => {
              setShowForm(false);
              setEditingEvent(null);
            }}
            requireImage={!editingEvent}
          />
        ) : loading ? (
          <motion.div
            className="loading-container"
            style={{ textAlign: "center", padding: "100px" }}
          >
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            >
              <FiLoader size={50} />
            </motion.div>
            <p>Yuklanmoqda...</p>
          </motion.div>
        ) : events.length === 0 ? (
          <motion.div
            className="empty-container"
            style={{ textAlign: "center", padding: "120px", color: "#666" }}
          >
            <h2>Hozircha tadbir yo‘q</h2>
            <p>"Tadbir qo‘shish" tugmasini bosing va birinchisini yarating</p>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <motion.table
              className="banner-table"
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, duration: 0.4 }}
            >
              <thead>
                <tr>
                  <th>№</th>
                  <th>Rasm</th>
                  <th>Sarlavha</th>
                  <th>Sana</th>
                  <th>Joylashuv</th>
                  <th>Status</th>
                  <th>Amallar</th>
                </tr>
              </thead>
              <tbody>
                {events.map((e, i) => (
                  <motion.tr
                    key={e.id}
                    layout
                    initial={{
                      opacity: 0,
                      x: -50,
                      scale: 0.9,
                    }}
                    animate={{
                      opacity: 1,
                      x: 0,
                      scale: 1,
                    }}
                    exit={{
                      opacity: 0,
                      x: 50,
                      scale: 0.9,
                    }}
                    transition={{
                      duration: 0.2,
                      type: "spring",
                      stiffness: 100,
                      delay: i * 0.05,
                    }}
                    className="animated-row"
                  >
                    <motion.td
                      initial={{ x: -20 }}
                      animate={{ x: 0 }}
                      transition={{ delay: i * 0.1 + 0.1 }}
                    >
                      {i + 1}
                    </motion.td>

                    <motion.td
                      initial={{ x: -20 }}
                      animate={{ x: 0 }}
                      transition={{ delay: i * 0.1 + 0.15 }}
                    >
                      <motion.div
                        className="avatar-wrapper"
                        whileHover={{ scale: 1.1, rotate: 5 }}
                        transition={{ duration: 0.2 }}
                      >
                        {e.imageUrl ? (
                          <motion.img
                            src={
                              e.imageUrl.startsWith("http")
                                ? e.imageUrl
                                : `${API_BASE}/${e.imageUrl}`
                            }
                            alt="event"
                            className="event-image"
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ delay: i * 0.1 + 0.2 }}
                            whileHover={{ scale: 1.15 }}
                            style={{
                              width: 60,
                              height: 60,
                              borderRadius: 8,
                              objectFit: "cover",
                            }}
                            onError={(e) =>
                              (e.target.src = "https://via.placeholder.com/60")
                            }
                          />
                        ) : (
                          <motion.div
                            className="no-image"
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ delay: i * 0.1 + 0.2 }}
                          >
                            —
                          </motion.div>
                        )}
                      </motion.div>
                    </motion.td>

                    <motion.td
                      className="title-cell"
                      initial={{ x: -20 }}
                      animate={{ x: 0 }}
                      transition={{ delay: i * 0.1 + 0.25 }}
                    >
                      <motion.span
                        whileHover={{ color: "#667eea" }}
                        transition={{ duration: 0.2 }}
                      >
                        {e.title}
                      </motion.span>
                    </motion.td>

                    <motion.td
                      initial={{ x: -20 }}
                      animate={{ x: 0 }}
                      transition={{ delay: i * 0.1 + 0.3 }}
                    >
                      {e.startTime
                        ? new Date(e.startTime).toLocaleDateString("uz-UZ")
                        : "—"}
                    </motion.td>

                    <motion.td
                      initial={{ x: -20 }}
                      animate={{ x: 0 }}
                      transition={{ delay: i * 0.1 + 0.35 }}
                    >
                      {e.location || "—"}
                    </motion.td>

                    <motion.td
                      initial={{ x: -20 }}
                      animate={{ x: 0 }}
                      transition={{ delay: i * 0.1 + 0.4 }}
                    >
                      <span
                        className={`status-badge ${
                          e.statusName === "Created"
                            ? "pending"
                            : e.statusName === "Modified"
                            ? "modified"
                            : e.statusName === "Confirmed"
                            ? "active"
                            : "inactive"
                        }`}
                        style={{
                          padding: "6px 12px",
                          borderRadius: "20px",
                          fontSize: "13px",
                          fontWeight: "600",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}
                      >
                        {e.statusName === "Created" && "Kutilyapti"}
                        {e.statusName === "Modified" && "O‘zgartirildi"}
                        {e.statusName === "Confirmed" && "Faol"}
                        {e.statusName === "Cancelled" && "Bekor qilindi"}
                        {![
                          "Created",
                          "Modified",
                          "Confirmed",
                          "Cancelled",
                        ].includes(e.statusName) && e.statusName}
                      </span>
                    </motion.td>

                    <motion.td>
                      <EventActionsDropdown
                        event={e}
                        onEdit={() => handleEdit(e.id)}
                        onView={() => handleView(e.id)}
                        refreshList={fetchEvents}
                      />
                    </motion.td>
                  </motion.tr>
                ))}
              </tbody>
            </motion.table>
          </motion.div>
        )}
      </div>
      <EventViewModal
        isOpen={viewOpen}
        onClose={() => {
          setViewOpen(false);
          setSelectedEvent(null);
        }}
        event={selectedEvent}
        loading={viewLoading}
      />
    </>
  );
};

export default EventsControl;
